import React from 'react';

const shimmerKeyframes = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SkeletonBase = ({ width, height, borderRadius = '4px', className = '', style = {} }) => (
  <div
    className={`skeleton-base ${className}`}
    style={{
      width,
      height,
      borderRadius,
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite, pulse 2s infinite',
      position: 'relative',
      overflow: 'hidden',
      ...style
    }}
  />
);

export const TransactionSkeleton = ({ delay = 0 }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 0',
    borderBottom: '1px solid var(--gray-200)',
    animation: `fadeIn 0.5s ease-out ${delay}ms both`
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <SkeletonBase width="40px" height="40px" borderRadius="50%" />
      <div>
        <SkeletonBase width="120px" height="16px" style={{ marginBottom: '8px' }} />
        <SkeletonBase width="80px" height="12px" />
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <SkeletonBase width="60px" height="20px" />
      <SkeletonBase width="20px" height="20px" borderRadius="4px" />
      <SkeletonBase width="20px" height="20px" borderRadius="4px" />
    </div>
  </div>
);

export const DashboardCardSkeleton = ({ delay = 0 }) => (
  <div className="stat-card" style={{ animation: `fadeIn 0.5s ease-out ${delay}ms both` }}>
    <div className="stat-header">
      <SkeletonBase width="80px" height="14px" />
      <SkeletonBase width="20px" height="20px" borderRadius="50%" />
    </div>
    <SkeletonBase width="100px" height="24px" style={{ margin: '12px 0' }} />
    <SkeletonBase width="60px" height="12px" />
  </div>
);

export const FilterSkeleton = () => (
  <div className="card" style={{ marginBottom: '1.5rem' }}>
  <div className="auto-grid-min200" style={{ gap: '1rem', marginBottom: '1rem' }}>
      {[...Array(4)].map((_, i) => (
        <SkeletonBase key={i} width="100%" height="40px" borderRadius="6px" />
      ))}
    </div>
  <div className="auto-grid-min200" style={{ gap: '1rem' }}>
      {[...Array(4)].map((_, i) => (
        <div key={i}>
          <SkeletonBase width="60px" height="12px" style={{ marginBottom: '8px' }} />
          <SkeletonBase width="100%" height="36px" borderRadius="6px" />
        </div>
      ))}
    </div>
  </div>
);

export const StatsSkeleton = () => (
  <div className="grid-3" style={{ gap: '1rem', marginBottom: '2rem' }}>
    {[...Array(3)].map((_, i) => (
      <DashboardCardSkeleton key={i} delay={i * 100} />
    ))}
  </div>
);

export const TransactionListSkeleton = ({ count = 5 }) => (
  <div className="card">
    <div style={{ marginBottom: '1.5rem' }}>
      <SkeletonBase width="150px" height="20px" style={{ marginBottom: '0.5rem' }} />
    </div>
    {[...Array(count)].map((_, i) => (
      <TransactionSkeleton key={i} delay={i * 50} />
    ))}
  </div>
);

export const PageSkeleton = () => (
  <div>
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <SkeletonBase width="200px" height="32px" />
        <SkeletonBase width="140px" height="36px" borderRadius="6px" />
      </div>
      <SkeletonBase width="300px" height="16px" />
    </div>
    <StatsSkeleton />
    <FilterSkeleton />
    <TransactionListSkeleton />
  </div>
);

const SkeletonLoader = () => (
  <style>{shimmerKeyframes}</style>
);

export default SkeletonLoader;